/**
 * Shadow Mode Tracking Utilities
 *
 * Sends domain MAV shadow API calls and their results to Google Chat for debugging.
 */

import { isSendShadowMetricsEnabled } from '@/helpers/mavModeDetection';

const GOOGLE_CHAT_WEBHOOK_URL = process.env.VUE_APP_GOOGLE_CHAT_WEBHOOK_URL;
const ENVIRONMENT = process.env.VUE_APP_ENV || process.env.NODE_ENV || 'unknown';

const DOMAIN_SHADOW_API_PATH = '/shadow/tree/domain';

const MAX_JSON_LENGTH = 1500;

function environmentLine() {
  return `• Environment: \`${ENVIRONMENT}\`\n`;
}

/**
 * @param {*} value
 * @returns {string}
 */
function stringifyForChat(value) {
  if (value === undefined || value === null) {
    return '—';
  }

  try {
    const serialized = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
    if (serialized.length <= MAX_JSON_LENGTH) {
      return serialized;
    }
    return `${serialized.substring(0, MAX_JSON_LENGTH)}… (truncated)`;
  } catch (error) {
    return String(value);
  }
}

/**
 * @param {Object} headers
 * @returns {Object}
 */
function extractTrackingHeaders(headers = {}) {
  const normalized = headers.common ? { ...headers.common, ...headers } : { ...headers };
  const tracking = {};

  ['X-Shadow-Request-Id', 'X-Shadow-Source', 'X-Discovery-Id', 'X-User-Email'].forEach(key => {
    if (normalized[key] != null) {
      tracking[key] = normalized[key];
    }
  });

  return tracking;
}

/**
 * Send message to Google Chat
 * @param {Object} message - Google Chat message object
 */
async function sendToGoogleChat(message) {
  if (!isSendShadowMetricsEnabled()) {
    return;
  }

  if (!GOOGLE_CHAT_WEBHOOK_URL) {
    console.warn('[Shadow Mode Tracking] Google Chat webhook URL not configured');
    return;
  }

  try {
    await fetch(GOOGLE_CHAT_WEBHOOK_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(message),
    });
  } catch (error) {
    console.error('[Shadow Mode Tracking] Failed to send to Google Chat:', error);
  }
}

/**
 * Post every shadow API call and its result to the webhook.
 * @param {Object} params
 * @param {string} params.endpoint
 * @param {string} [params.method]
 * @param {*} [params.requestData]
 * @param {*} [params.responseData]
 * @param {Error} [params.error]
 * @param {number} [params.status]
 * @param {number} [params.durationMs]
 * @param {Object} [params.headers]
 */
export async function trackShadowApiCall({
  endpoint,
  method = 'POST',
  requestData,
  responseData,
  error,
  status,
  durationMs,
  headers = {},
}) {
  const succeeded = !error;
  const statusCode = status || error?.response?.status || (succeeded ? 200 : 'N/A');
  const resultPayload = succeeded ? responseData : responseData ?? error?.response?.data;
  const errorMessage = error?.message;

  const logPayload = {
    environment: ENVIRONMENT,
    endpoint,
    method: method?.toUpperCase(),
    status: statusCode,
    succeeded,
    durationMs,
    headers,
    request: requestData,
    response: resultPayload,
    errorMessage,
    timestamp: new Date().toISOString(),
  };

  console.log('[Shadow Mode Tracking] API call:', logPayload);

  const headerLines = Object.entries(headers)
    .map(([key, value]) => `• ${key}: ${value}`)
    .join('\n');

  const message = {
    text:
      `${succeeded ? '✅' : '❌'} *Shadow API ${succeeded ? 'Success' : 'Failed'}*\n\n` +
      environmentLine() +
      `• Endpoint: \`${method?.toUpperCase() || 'POST'} ${endpoint}\`\n` +
      `• Status: ${statusCode}\n` +
      (durationMs != null ? `• Duration: ${durationMs}ms\n` : '') +
      (errorMessage ? `• Error: ${errorMessage}\n` : '') +
      (headerLines ? `${headerLines}\n` : '') +
      `• Time: ${new Date().toLocaleString()}\n\n` +
      `*Request:*\n\`\`\`\n${stringifyForChat(requestData)}\n\`\`\`\n\n` +
      `*Response:*\n\`\`\`\n${stringifyForChat(resultPayload)}\n\`\`\``,
  };

  await sendToGoogleChat(message);
}

/**
 * Register axios interceptors on the MAV client to track domain shadow API calls only.
 * @param {import('axios').AxiosInstance} axiosInstance
 */
export function installShadowApiTracking(axiosInstance) {
  if (axiosInstance.__shadowTrackingInstalled) return;
  axiosInstance.__shadowTrackingInstalled = true;

  const isDomainShadowUrl = url => typeof url === 'string' && url.includes(DOMAIN_SHADOW_API_PATH);

  axiosInstance.interceptors.request.use(
    config => {
      if (isDomainShadowUrl(config.url)) {
        config.metadata = {
          ...(config.metadata || {}),
          shadowTracking: {
            startTime: Date.now(),
            requestData: config.data,
          },
        };
      }
      return config;
    },
    error => Promise.reject(error),
  );

  axiosInstance.interceptors.response.use(
    response => {
      const tracking = response.config?.metadata?.shadowTracking;
      if (tracking) {
        trackShadowApiCall({
          endpoint: response.config.url,
          method: response.config.method,
          requestData: tracking.requestData,
          responseData: response.data,
          status: response.status,
          durationMs: Date.now() - tracking.startTime,
          headers: extractTrackingHeaders(response.config.headers),
        });
      }
      return response;
    },
    error => {
      const config = error.config;
      const tracking = config?.metadata?.shadowTracking;

      if (tracking) {
        trackShadowApiCall({
          endpoint: config.url,
          method: config.method,
          requestData: tracking.requestData,
          responseData: error.response?.data,
          error,
          status: error.response?.status,
          durationMs: Date.now() - tracking.startTime,
          headers: extractTrackingHeaders(config.headers),
        });
      }

      return Promise.reject(error);
    },
  );
}

/**
 * Track when shadow mode evaluation is attempted on save (correlation with submitClassify).
 * @param {Object} context
 * @param {boolean} shadowModeEnabled
 * @param {Object} mavConfig
 */
export async function trackShadowModeAttempt(context, shadowModeEnabled, mavConfig) {
  const trackingData = {
    isShadowMode: shadowModeEnabled,
    domain: context.domain,
    treeId: context.treeId,
    discoveryId: context.discoveryId,
    entityType: context.entityType || 'domain',
    userEmail: context.userEmail,
    timestamp: new Date().toISOString(),
    config: {
      shadow: mavConfig?.enabled?.shadow,
      v1: mavConfig?.enabled?.v1,
      v2: mavConfig?.enabled?.v2,
    },
  };

  console.log('[Shadow Mode Tracking] Save attempt:', trackingData);

  if (!shadowModeEnabled) {
    const message = {
      text:
        `⚠️ *Shadow Mode Disabled During Save*\n\n` +
        environmentLine() +
        `• Domain: \`${context.domain}\`\n` +
        `• Tree ID: ${context.treeId}\n` +
        `• Discovery ID: ${context.discoveryId}\n` +
        `• User: ${context.userEmail}\n` +
        `• Config: shadow=${mavConfig?.enabled?.shadow}, v1=${mavConfig?.enabled?.v1}, v2=${mavConfig?.enabled?.v2}\n` +
        `• Time: ${new Date().toLocaleString()}`,
    };
    await sendToGoogleChat(message);
  }
}

/**
 * @param {Object} context
 */
export function trackShadowModeSuccess(context) {
  console.log('[Shadow Mode Tracking] Save shadow evaluation completed:', {
    domain: context.domain,
    treeId: context.treeId,
    discoveryId: context.discoveryId,
    entityType: context.entityType || 'domain',
    timestamp: new Date().toISOString(),
  });
}

/**
 * @param {Object} context
 * @param {Error} error
 */
export function trackShadowModeError(context, error) {
  console.error('[Shadow Mode Tracking] Save shadow evaluation failed:', {
    domain: context.domain,
    treeId: context.treeId,
    discoveryId: context.discoveryId,
    entityType: context.entityType || 'domain',
    errorMessage: error.message,
    errorStatus: error.response?.status,
    timestamp: new Date().toISOString(),
  });
}

/**
 * @param {Object} context
 */
export function trackSubmitClassify(context) {
  console.log('[Shadow Mode Tracking] submitClassify:', {
    domain: context.domain,
    treeId: context.treeId,
    discoveryId: context.discoveryId,
    entityType: context.entityType || 'domain',
    valid: context.valid,
    timestamp: new Date().toISOString(),
  });
}

/**
 * @returns {string}
 */
export function generateRequestId() {
  return `${Date.now()}-${Math.random()
    .toString(36)
    .substr(2, 9)}`;
}
